import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollViewProps, ViewStyle } from 'react-native';
import { useInterpret } from '@xstate/react';
import { RecyclerListView, DataProvider, LayoutProvider, RecyclerListViewProps } from 'recyclerlistview';
import { RecyclerListViewState } from 'recyclerlistview/dist/reactnative/core/RecyclerListView';
import { Page } from '@alucio/aws-beacon-amplify/src/models';
import { ContentPageData, getS3KeysWithinDocumentVersionORM } from 'src/hooks/useContentPageData/useContentPageData';
import useLazyRef from 'src/hooks/useLazyRef';
import useMachineSelector, { composite } from 'src/hooks/useSelector';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { DocumentVersionORM } from 'src/types/orms';
import * as relatedSlideSelector from 'src/state/machines/relatedSlide/relatedSlide.selectors';
import { relatedSlideSM } from 'src/state/machines/relatedSlide/relatedSlide.machine';
import { RelatedSlideData, RelatedSlideService } from 'src/state/machines/relatedSlide/relatedSlide.types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import { useContentPreviewModal } from 'src/components/ContentPreviewModalV2/State/ContentPreviewModalStateProvider';
import { SidebarOptionsEnum, useSidebar } from 'src/components/ContentPreviewModalV2/SideBar/SidebarProvider';
import { fetchPagesJson } from 'src/utils/loadCloudfrontAsset/common';
import { getDocumentVersionIdFromPageId, isDocumentSealed } from 'src/utils/documentHelpers';
import { getCurrentDocVer } from '../sharing/shareUtils';
import * as logger from 'src/utils/logger';

/** RECYCLERLISTVIEW TYPES */
type RViewProps = {
  layoutProvider: LayoutProvider,
  dataProvider: DataProvider,
  rowRenderer: RecyclerListViewProps['rowRenderer'],
  style: ViewStyle,
  canChangeSize: boolean,
  scrollViewProps: ScrollViewProps,
}

export interface relatedSlideRenderData extends RelatedSlideData {
  documentVersionORM: DocumentVersionORM
  page: Page
  thumbnailTitle: string
}

type IRelatedSlideContext = {
  service: RelatedSlideService,
  scrollViewRef: React.RefObject<RecyclerListView<RViewProps, RecyclerListViewState>>,
  currentRelatedSlidesData: relatedSlideRenderData[],
  onErrorRetry: () => void,
  onSelectedRelatedSlide: (documentVersionORM: DocumentVersionORM, pageNumber: number) => void,
}

const RelatedSlideContext = React.createContext<IRelatedSlideContext>(undefined!)
export const useRelatedSlide = () => useContext(RelatedSlideContext)

const RelatedSlideProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const { isOnline } = useAppSettings();
  const { activePresentation, setActiveSlideByPresentationPageNumber, addPresentation } = useContent();
  const { setIsAssociatedFile } = useContentPreviewModal();
  const { selectedSideBarOption } = useSidebar();
  const enableMatchSlides = useFeatureFlags('BEAC_4251_slide_version_tracking');
  const scrollViewRef = useRef<RecyclerListView<RViewProps, RecyclerListViewState>>(null);
  const [allContentPageDataVersions, setAllContentPageDataVersions] =
    useState<{[documentVersionId: string] : ContentPageData[]}>({});

  /** STATE MACHINE */
  const machineInstance = useLazyRef(() => relatedSlideSM.withContext({
    relatedSlides: {},
    currentPageId: activePresentation?.currentPresentablePage.id,
    retryAttempt: 0,
  }))

  const service = useInterpret(
    machineInstance.current!,
    // { devTools: true },
  )

  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      relatedSlideSelector.isIdleState,
      relatedSlideSelector.currentRelatedSlides,
    ),
  )

  const currentRelatedSlidesData = useMemo(() => {
    return cond.currentRelatedSlides.reduce<relatedSlideRenderData[]>((acc, cur) => {
      const { documentVersionId, pageNumber } = getDocumentVersionIdFromPageId(cur.pageId);
      if (!documentVersionId || !pageNumber) return acc;
      const documentVersionORM = getCurrentDocVer(documentVersionId)
      if (!documentVersionORM) {
        logger.contentPresenting.CPM.relatedSlides.warn('Unable to find documentVerionORM from redux state');
        return acc;
      }
      if (isDocumentSealed(documentVersionORM.relations.documentORM)) return acc;
      const page = documentVersionORM.meta.allPages[pageNumber - 1];
      if (!documentVersionORM || !page) return acc;
      else {
        const contentPageData = allContentPageDataVersions[documentVersionORM.model.id];
        const thumbnailTitle = contentPageData?.[page.number - 1]?.title ?? '';
        return [...acc, { ...cur, documentVersionORM, page, thumbnailTitle }]
      }
    }, [])
  }, [cond.currentRelatedSlides, allContentPageDataVersions])

  /* FUNCTIONS START */
  const onToggleTabVisibility = useCallback((visibility: boolean) => {
    if (visibility) service.send({ type: 'OPEN_RELATED_SLIDES_TAB' });
    else service.send({ type: 'CLOSE_RELATED_SLIDES_TAB' });
  }, [service])

  const onToggleOnlineStatus = useCallback((isOnline: boolean) => {
    if (isOnline) service.send({ type: 'SWITCH_ONLINE' });
    else service.send({ type: 'SWITCH_OFFLINE' });
  }, [service])

  const onSwitchPage = useCallback((currentPageId: string) => {
    service.send({ type: 'SWITCH_PAGE', currentPageId });
  }, [service])

  const onErrorRetry = useCallback(() => {
    service.send({ type: 'USER_RETRY' });
  }, [service])

  const getAllContentPageDataVersions = useCallback( async (s3Mapping: {[documentVersionId: string]: string}) => {
    const responses = await fetchPagesJson({ ...s3Mapping });
    const result = responses.reduce<{ [documentVersionId: string] : ContentPageData[]}>((acc, response) => {
      const documentVersionORM = getCurrentDocVer(response.documentVersionId);
      if (!documentVersionORM) {
        logger.contentPresenting.CPM.relatedSlides.error('Unable to find documentVerionORM from redux state');
        return acc;
      }
      if (!acc[response.documentVersionId]) {
        const items = response.content.pages.map(page => {
          if (!page) {
            logger.contentPresenting.CPM.relatedSlides.warn('Page is null', response)
            return null
          }
          const pageModel = documentVersionORM.relations.pages[page.number - 1]?.model
          if (!pageModel) {
            logger.contentPresenting.CPM.relatedSlides.verbose(
              `Page model not found for page number ${page.number}`,
            )
            return null
          }
          const rv: ContentPageData = {
            content: page.content,
            documentPageNumber: page.number,
            presentationPageNumber: page.number,
            speakerNotes: page.overrideSpeakerNotes ?? page.speakerNotes,
            s3Key: detectArchivedFileKeyPath(documentVersionORM.model, pageModel, 'sm') || '',
            title: page.overrideTitle ?? page.title,
            htmlUrl: page.htmlUrl,
            htmlPageImage: page.htmlPageImage,
            recommendations: page.recommendations ?? [],
            mapping: enableMatchSlides ? (page.mapping ?? null) : null,
            pageId: pageModel.pageId,
            enableSlideTextInsertion: !!page.enableSlideTextInsertion,
          }

          return rv
        })
          .filter((page): page is ContentPageData => page !== null)

        acc[response.documentVersionId] = items
      }
      return acc;
    }, {})
    setAllContentPageDataVersions(prev => ({
      ...prev,
      ...result,
    }));
  }, [setAllContentPageDataVersions])

  const onSelectedRelatedSlide = useCallback((documentVersionORM: DocumentVersionORM, pageNumber: number) => {
    if (activePresentation?.currentPresentablePage.documentVersionORM.model.id === documentVersionORM.model.id) {
      // SAME DOCUMENT
      setActiveSlideByPresentationPageNumber(pageNumber);
    } else {
      // DIFFERENT DOCUMENT
      setIsAssociatedFile(true)
      addPresentation(documentVersionORM, pageNumber)
    }
  }, [activePresentation, setActiveSlideByPresentationPageNumber, setIsAssociatedFile, addPresentation])
  /* FUNCTIONS END */

  /* USEEFFECT START */
  useEffect(() => {
    // TOGGLE TAB VISIBILITY
    if (selectedSideBarOption === SidebarOptionsEnum.relatedSlides) onToggleTabVisibility(true);
    else if (!cond.isIdleState) onToggleTabVisibility(false);
  }, [selectedSideBarOption, cond.isIdleState, onToggleTabVisibility])

  useEffect(() => onToggleOnlineStatus(isOnline), [isOnline])

  useEffect(() => {
    // PAGE CHANGED
    if (activePresentation?.currentPresentablePage.page.pageId) {
      onSwitchPage(activePresentation?.currentPresentablePage.page.pageId)
    } else logger.contentPresenting.CPM.relatedSlides.warn('Attempt to switch to an undefined pageId ')
  }, [activePresentation?.currentPresentablePage.page.pageId])

  useEffect(() => {
    const documentVersionToFetch = cond.currentRelatedSlides.reduce<DocumentVersionORM[]>((acc, cur) => {
      const { documentVersionId } = getDocumentVersionIdFromPageId(cur.pageId);
      if (!documentVersionId || acc.find(docVer => docVer.model.id === documentVersionId)) return acc;
      const documentVersionORM = getCurrentDocVer(documentVersionId);
      if (documentVersionORM && !allContentPageDataVersions[documentVersionORM.model.id]) {
        return [...acc, documentVersionORM]
      } else return acc;
    }, [])

    if (documentVersionToFetch.length) {
      const s3Mapping = getS3KeysWithinDocumentVersionORM(documentVersionToFetch);
      getAllContentPageDataVersions(s3Mapping);
    }
  }, [cond.currentRelatedSlides, getAllContentPageDataVersions, allContentPageDataVersions])
  /* USEEFFECT END */

  const contextValue: IRelatedSlideContext = {
    service,
    scrollViewRef,
    currentRelatedSlidesData,
    onErrorRetry,
    onSelectedRelatedSlide,
  }

  return (
    <RelatedSlideContext.Provider value={contextValue}>
      {children}
    </RelatedSlideContext.Provider>
  )
}

export default RelatedSlideProvider;
